import React, { useState, useRef, useEffect } from "react";
import { Grid, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { Button, ScrollToTop } from "components";
import { DotIcon, DownloadIcon, BackIcon, ConfirmFailIcon } from "icons";
import { mplusFont, localStorageHelper } from "utils";

import {
  FullStressScoringActionCreators,
  MiniStressScoringActionCreators,
} from "redux/rootActions";
import {
  selectFullStressErrorMessage,
  fullStressScoringResponse,
} from "redux/stressCheck/fullStressScoringInfo/fullStressScoringInfoStates";
import {
  selectMiniStressErrorMessage,
  miniStressScoringResponse,
} from "redux/stressCheck/miniStressScoringInfo/miniStressScoringInfoStates";

import {
  TextTitle,
  TotalHeader,
  TableWrapper,
  FirstTable,
  SecondTable,
  TableContainer,
  NoteText,
  StressSituationTitleWrapper,
  StressSituationContentWrapper,
  TableStatisticsWrapper,
} from "./Stress.style";

const { useBreakpoint } = Grid;

interface IStressSituationDetails {
  handleCancel: () => void;
}

const StressSituationDetails: React.FC<IStressSituationDetails> = ({
  handleCancel,
}) => {
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });

  const location = useLocation();
  const dispatch = useDispatch();
  const fullStressScoring = useSelector(fullStressScoringResponse);
  const miniStressScoring = useSelector(miniStressScoringResponse);
  const fullStressScoringInfoError = useSelector(selectFullStressErrorMessage);
  const miniStressScoringInfoError = useSelector(selectMiniStressErrorMessage);

  const imgRef = useRef<any>();
  const [detailInformation, setDetailInformation] = useState<any>({});

  const isFullStressCheck = React.useMemo(() => {
    return location.pathname === "/stress-59-situation";
  }, [location.pathname]);

  const screens = useBreakpoint();

  const handleScoringInfoDownloadPDF = async () => {
    const canvas = await html2canvas(imgRef.current);

    const data = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "px", "a4");
    const pageMargin = 12;
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    pdf.addImage(
      data,
      "png",
      pageMargin,
      pageMargin,
      width - 2 * pageMargin,
      height - 2 * pageMargin
    );
    pdf.setDrawColor(178, 177, 185);
    pdf.addFileToVFS("mplus.ttf", mplusFont);
    pdf.addFont("mplus.ttf", "mplus", "normal");
    pdf.setFont("mplus", "normal");
    if (detailInformation?.user_information) {
      pdf.save(`${detailInformation?.user_information[0]?.name}-採点情報`);
    }
  };

  useEffect(() => {
    if (fullStressScoring && Object.keys(fullStressScoring.result).length) {
      setDetailInformation(fullStressScoring.result);
    }
  }, [fullStressScoring]);

  useEffect(() => {
    if (miniStressScoring && Object.keys(miniStressScoring.result).length) {
      setDetailInformation(miniStressScoring.result);
    }
  }, [miniStressScoring]);

  useEffect(() => {
    if (fullStressScoringInfoError) {
      notification.error({
        placement: "bottomRight",
        message: fullStressScoringInfoError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(FullStressScoringActionCreators.handleResetAction());
    }
  }, [fullStressScoringInfoError]);

  useEffect(() => {
    if (miniStressScoringInfoError) {
      notification.error({
        placement: "bottomRight",
        message: miniStressScoringInfoError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(MiniStressScoringActionCreators.handleResetAction());
    }
  }, [miniStressScoringInfoError]);

  return (
    <>
      <ScrollToTop />
      <StressSituationTitleWrapper>
        <BackIcon className="back" onClick={handleCancel} />
        <div
          style={{
            fontSize: 16,
            lineHeight: "23px",
            fontWeight: "bold",
            marginLeft: 2,
          }}
        >
          {isFullStressCheck ? t("full-stress") : t("mini-stress")}
        </div>
      </StressSituationTitleWrapper>
      <StressSituationContentWrapper>
        <TotalHeader>
          <TextTitle>{t1("scoring-information")}</TextTitle>
          <Button
            icon={
              <DownloadIcon
                width="14px"
                height="14px"
                fill="currentColor"
                style={{ position: "absolute", left: 4, top: 4 }}
              />
            }
            name={t2("pdf-output")}
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={12}
            lineHeight="17px"
            fontWeight={700}
            padding="3px 10px 3px 16px"
            onClick={handleScoringInfoDownloadPDF}
          />
        </TotalHeader>
        <div ref={imgRef}>
          <TableWrapper>
            <FirstTable>
              <div className="first-line">{t1("user-information")}</div>
              <div className="second-line">
                <div>
                  {t1("name")}
                  <span style={{ fontWeight: 500 }}>
                    {detailInformation?.user_information &&
                      detailInformation?.user_information[0]?.name}
                  </span>
                </div>
                <div>
                  {t1("age")}{" "}
                  <span style={{ fontWeight: 500 }}>
                    {detailInformation?.user_information &&
                      detailInformation?.user_information[0]?.age?.split(
                        "歳"
                      )[0]}
                    {t1("ages")}
                  </span>
                </div>
              </div>
              <div className="third-line">
                <div>
                  {t("company-affiliation")}
                  <span style={{ fontWeight: 500 }}>
                    {detailInformation?.user_information &&
                      detailInformation?.user_information[0]?.designation}
                  </span>
                </div>
                <div>
                  {t2("gender")} :
                  <span style={{ fontWeight: 500 }}>
                    {detailInformation?.user_information &&
                      detailInformation?.user_information[0]?.gender}
                  </span>
                </div>
              </div>
              <div className="fourth-line">
                {t("stress-judment-result")}
                <span style={{ fontWeight: 500 }}>
                  {detailInformation?.point_information &&
                    detailInformation?.point_information[0]
                      ?.stress_assessment_result}
                </span>
              </div>
            </FirstTable>
            <SecondTable>
              <div className="first-line line">
                <div>{t1("item")}</div>
                <div>{t1("rating-points")}</div>
              </div>
              <div className=" line">
                <div>{t("items-related-to-stress-factors")}</div>
                <div>
                  {detailInformation?.point_information &&
                    detailInformation?.point_information[0]?.cause_of_stress}
                  <span>{t1("point")}</span>
                </div>
              </div>
              <div className=" line">
                <div>{t("items-related-to-mental-and-physical")}</div>
                <div>
                  {detailInformation?.point_information &&
                    detailInformation?.point_information[0]?.mental_physical}
                  <span>{t1("point")}</span>
                </div>
              </div>
              <div className=" line">
                <div>{t("peripheral-support-items")}</div>
                <div>
                  {detailInformation?.point_information &&
                    detailInformation?.point_information[0]?.stress_factor}
                  <span>{t1("point")}</span>
                </div>
              </div>
              <div className=" line">
                <div>{t1("total")}</div>
                <div>
                  {detailInformation?.point_information &&
                    detailInformation?.point_information[0]?.total}
                  <span>{t1("point")}</span>
                </div>
              </div>
            </SecondTable>
          </TableWrapper>
          <TableStatisticsWrapper>
            <TableContainer>
              <div className="header line">
                <div>{t1("measure")}</div>
                <div>
                  <div>
                    {currentLanguage === "vi" ? "Thấp / ít" : "低い / 少い"}
                  </div>
                  <div>
                    {currentLanguage === "vi"
                      ? "Hơi thấp / ít"
                      : "やや低い / 少い"}
                  </div>
                  <div>{currentLanguage === "vi" ? "Bình thường" : "普通"}</div>
                  <div>
                    {currentLanguage === "vi"
                      ? "Hơi cao / nhiều"
                      : "やや高い / 多い"}
                  </div>
                  <div>
                    {currentLanguage === "vi" ? "Cao / nhiều" : "高い / 多い"}
                  </div>
                </div>
                <div>{t1("evaluation-point")}</div>
              </div>
              <div className="title">
                【
                {currentLanguage === "vi"
                  ? "Các yếu tố  gây ra căng thẳng"
                  : "ストレスの原因と考えられる因子"}
                】
              </div>
              <div className="evaluation">
                <div>
                  <div>{t1("evaluation-point")}</div>
                </div>
                <div>
                  <div>5</div>
                  <div>4</div>
                  <div>3</div>
                  <div>2</div>
                  <div>1</div>
                </div>
                <div></div>
              </div>
              {detailInformation?.stress_results?.group1?.order_5_to_1 &&
                detailInformation?.stress_results?.group1?.order_5_to_1.map(
                  (item: any, index: number) => (
                    <div className="rating" key={index}>
                      <div>{item?.name}</div>
                      <div>
                        <div>{item?.low && <DotIcon />}</div>
                        <div>{item?.slightly_low && <DotIcon />}</div>
                        <div>{item?.normal && <DotIcon />}</div>
                        <div>{item?.slightly_high && <DotIcon />}</div>
                        <div className="background">
                          {item?.high && <DotIcon />}
                        </div>
                      </div>
                      <div>{item?.rating_point}</div>
                    </div>
                  )
                )}
              {detailInformation?.stress_results?.group8?.order_5_to_1 &&
                detailInformation?.stress_results?.group8?.order_5_to_1.map(
                  (item: any, index: number) => (
                    <div className="rating" key={index}>
                      <div>{item?.name}</div>
                      <div>
                        <div>{item?.low && <DotIcon />}</div>
                        <div>{item?.slightly_low && <DotIcon />}</div>
                        <div>{item?.normal && <DotIcon />}</div>
                        <div>{item?.slightly_high && <DotIcon />}</div>
                        <div className="background">
                          {item?.high && <DotIcon />}
                        </div>
                      </div>
                      <div>{item?.rating_point}</div>
                    </div>
                  )
                )}
              <div className="evaluation">
                <div>
                  <div>{t1("evaluation-point")}</div>
                </div>
                <div>
                  <div>1</div>
                  <div>2</div>
                  <div>3</div>
                  <div>4</div>
                  <div>5</div>
                </div>
                <div></div>
              </div>
              {detailInformation?.stress_results?.group1?.order_1_to_5 &&
                detailInformation?.stress_results?.group1?.order_1_to_5.map(
                  (item: any, index: number) => (
                    <div className="rating" key={index}>
                      <div>{item?.name}</div>
                      <div>
                        <div className="background">
                          {item?.low && <DotIcon />}
                        </div>
                        <div>{item?.slightly_low && <DotIcon />}</div>
                        <div>{item?.normal && <DotIcon />}</div>
                        <div>{item?.slightly_high && <DotIcon />}</div>
                        <div>{item?.high && <DotIcon />}</div>
                      </div>
                      <div>{item?.rating_point}</div>
                    </div>
                  )
                )}
              {detailInformation?.stress_results?.group8?.order_1_to_5 &&
                detailInformation?.stress_results?.group8?.order_1_to_5.map(
                  (item: any, index: number) => (
                    <div className="rating" key={index}>
                      <div>{item?.name}</div>
                      <div>
                        <div className="background">
                          {item?.low && <DotIcon />}
                        </div>
                        <div>{item?.slightly_low && <DotIcon />}</div>
                        <div>{item?.normal && <DotIcon />}</div>
                        <div>{item?.slightly_high && <DotIcon />}</div>
                        <div>{item?.high && <DotIcon />}</div>
                      </div>
                      <div>{item?.rating_point}</div>
                    </div>
                  )
                )}

              {isFullStressCheck && (
                <>
                  <div className="title">
                    【
                    {currentLanguage === "vi"
                      ? "Phản ứng sinh lý và tâm lý do căng thẳng gây ra"
                      : "ストレスによっておこる心身の反応"}
                    】
                  </div>
                  <div className="evaluation">
                    <div>
                      <div>{t1("evaluation-point")}</div>
                    </div>
                    <div>
                      <div>1</div>
                      <div>2</div>
                      <div>3</div>
                      <div>4</div>
                      <div>5</div>
                    </div>
                    <div></div>
                  </div>
                  {detailInformation?.stress_results?.group2?.order_1_to_5 &&
                    detailInformation?.stress_results?.group2?.order_1_to_5.map(
                      (item: any, index: number) => (
                        <div className="rating" key={index}>
                          <div>{item?.name}</div>
                          <div>
                            <div className="background">
                              {item?.low && <DotIcon />}
                            </div>
                            <div>{item?.slightly_low && <DotIcon />}</div>
                            <div>{item?.normal && <DotIcon />}</div>
                            <div>{item?.slightly_high && <DotIcon />}</div>
                            <div>{item?.high && <DotIcon />}</div>
                          </div>
                          <div>{item?.rating_point}</div>
                        </div>
                      )
                    )}
                </>
              )}
              <div className="evaluation">
                <div>
                  <div>{t1("evaluation-point")}</div>
                </div>
                <div>
                  <div>5</div>
                  <div>4</div>
                  <div>3</div>
                  <div>2</div>
                  <div>1</div>
                </div>
                <div></div>
              </div>
              {isFullStressCheck ? (
                <>
                  {detailInformation?.stress_results?.group2?.order_5_to_1 &&
                    detailInformation?.stress_results?.group2?.order_5_to_1.map(
                      (item: any, index: number) => (
                        <div className="rating" key={index}>
                          <div>{item?.name}</div>
                          <div>
                            <div>{item?.low && <DotIcon />}</div>
                            <div>{item?.slightly_low && <DotIcon />}</div>
                            <div>{item?.normal && <DotIcon />}</div>
                            <div>{item?.slightly_high && <DotIcon />}</div>
                            <div className="background">
                              {item?.high && <DotIcon />}
                            </div>
                          </div>
                          <div>{item?.rating_point}</div>
                        </div>
                      )
                    )}
                </>
              ) : (
                <>
                  {detailInformation?.stress_results?.group9?.order_5_to_1 &&
                    detailInformation?.stress_results?.group9?.order_5_to_1.map(
                      (item: any, index: number) => (
                        <div className="rating" key={index}>
                          <div>{item?.name}</div>
                          <div>
                            <div>{item?.low && <DotIcon />}</div>
                            <div>{item?.slightly_low && <DotIcon />}</div>
                            <div>{item?.normal && <DotIcon />}</div>
                            <div>{item?.slightly_high && <DotIcon />}</div>
                            <div className="background">
                              {item?.high && <DotIcon />}
                            </div>
                          </div>
                          <div>{item?.rating_point}</div>
                        </div>
                      )
                    )}
                </>
              )}
              <div className="title">
                【
                {currentLanguage === "vi"
                  ? "Các yếu tố khác ảnh hưởng đến phản ứng với stress"
                  : "ストレス反応に影響を与える他の因子"}
                】
              </div>
              <div className="evaluation">
                <div>
                  <div>{t1("evaluation-point")}</div>
                </div>
                <div>
                  <div>1</div>
                  <div>2</div>
                  <div>3</div>
                  <div>4</div>
                  <div>5</div>
                </div>
                <div></div>
              </div>
              {detailInformation?.stress_results?.group3?.order_1_to_5 &&
                detailInformation?.stress_results?.group3?.order_1_to_5.map(
                  (item: any, index: number) => (
                    <div className="rating" key={index}>
                      <div>{item?.name}</div>
                      <div>
                        <div className="background">
                          {item?.low && <DotIcon />}
                        </div>
                        <div>{item?.slightly_low && <DotIcon />}</div>
                        <div>{item?.normal && <DotIcon />}</div>
                        <div>{item?.slightly_high && <DotIcon />}</div>
                        <div>{item?.high && <DotIcon />}</div>
                      </div>
                      <div>{item?.rating_point}</div>
                    </div>
                  )
                )}
              {detailInformation?.stress_results?.group10?.order_1_to_5 &&
                detailInformation?.stress_results?.group10?.order_1_to_5.map(
                  (item: any, index: number) => (
                    <div className="rating" key={index}>
                      <div>{item?.name}</div>
                      <div>
                        <div className="background">
                          {item?.low && <DotIcon />}
                        </div>
                        <div>{item?.slightly_low && <DotIcon />}</div>
                        <div>{item?.normal && <DotIcon />}</div>
                        <div>{item?.slightly_high && <DotIcon />}</div>
                        <div>{item?.high && <DotIcon />}</div>
                      </div>
                      <div>{item?.rating_point}</div>
                    </div>
                  )
                )}
            </TableContainer>
          </TableStatisticsWrapper>
        </div>
        <NoteText>
          {currentLanguage === "vi"
            ? '※Trong bảng quy đổi điểm, có mục "Mức độ hài lòng với công việc và cuộc sống", tuy nhiên mục này không được sử dụng khi xác định đối tượng có mức độ stress cao. '
            : "※素点換算表には「仕事や生活の満足度」の項目がありますが、高ストレス者の選定に当たっては使用しません。"}
        </NoteText>
        <NoteText>
          {currentLanguage === "vi"
            ? "※Những vùng xám mà có tích ● càng nhiều、 có nghĩa là trạng thái stress đang xấu."
            : "※グレー部分に●があるほど、ストレス状態は良くないことを表しています。"}
        </NoteText>
      </StressSituationContentWrapper>
    </>
  );
};

export default StressSituationDetails;
